'use client'

import Link from 'next/link'
import NextImage from 'next/image'
import { Menu } from 'lucide-react'
import { Button } from '@/core/components/button'
import { Separator } from '@/core/components/separator'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/core/components/sheet'
import { ShopLink } from '@/features/auth/components/shop-link.client'
import { routes } from '@/shared/routes'

const navClass = 'font-medium text-navy hover:text-primary'

/**
 * The guest header: the three products across the middle, sign-in on the
 * right. Port of the Lovable landing's `LandingHeader`. Fixed rather than
 * sticky so it can sit below the smart app banner; pages clear it with their
 * own top spacing. The banner offset is applied here as a variant because the
 * global `body.has-app-banner header.fixed { top }` rule lives in the base
 * layer and loses to the `top-0` utility — the header used to hide behind the
 * banner on phones.
 */
export function LandingHeader() {
  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-border bg-card/95 backdrop-blur [body.has-app-banner_&]:top-14">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link href={routes.root} className="flex items-center space-x-2 transition-opacity hover:opacity-80">
          {/* Rendered size, not the source's intrinsic size — see the note in
              `app-layout.client.tsx` (PB-733). */}
          <NextImage src="/hubhive-logo.png" alt="HubHive" width={168} height={49} className="h-8 w-auto" />
        </Link>

        <nav aria-label="Primary navigation" className="hidden flex-1 items-center justify-center gap-8 md:flex">
          <Link href={routes.hives} className={navClass}>
            Hives
          </Link>
          <ShopLink href={routes.shopSite.index()} className={navClass}>
            Shop
          </ShopLink>
          <a href={routes.profinder.index()} className={navClass}>
            ProFinder
          </a>
        </nav>

        <div className="hidden items-center space-x-3 md:flex">
          <Link href={routes.login()} className="text-sm font-medium text-navy hover:text-primary">
            Sign in
          </Link>
          <Button asChild className="px-5">
            <Link href={routes.signup}>Get started</Link>
          </Button>
        </div>

        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-6 w-6 text-navy" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-card">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <nav className="mt-8 flex flex-col space-y-5">
                <Link href={routes.hives} className="text-lg font-medium text-navy">
                  Hives
                </Link>
                <ShopLink href={routes.shopSite.index()} className="text-lg font-medium text-navy">
                  Shop
                </ShopLink>
                <a href={routes.profinder.index()} className="text-lg font-medium text-navy">
                  ProFinder
                </a>
                <Separator />
                <Link href={routes.productMarketing} className="font-medium text-muted-foreground">
                  Product
                </Link>
                <Link href={routes.forBusinesses} className="font-medium text-muted-foreground">
                  For Businesses
                </Link>
                <Link href={routes.about} className="font-medium text-muted-foreground">
                  About
                </Link>
                <Link href={routes.login()} className="font-medium text-muted-foreground">
                  Sign in
                </Link>
                <div className="pt-6">
                  <Button asChild className="w-full">
                    <Link href={routes.signup}>Get started</Link>
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
